import React from "react";
import SearchSelect from "components/ui/SearchSelect";
import { useGetOfficesQuery } from "apis/office";

const EMPTY = { mode: "all", offices: [] };

const MODE_OPTIONS = [
  { value: "all", label: "All offices" },
  { value: "specific", label: "Specific offices only" },
];

/**
 * "Available to" — which offices a product is offered to.
 *
 *   All offices      → no restriction
 *   Specific offices → only the selected offices
 *
 * The product stores just `offices: []` (empty = all). `mode` lives in the
 * form only, so "restricted but nothing picked yet" is a state the caller can
 * reject on submit rather than silently saving as unrestricted.
 *
 * Controlled value: { mode: "all" | "specific", offices: string[] }
 *
 * `offices` narrows the list to a caller-supplied set — package rules pass the
 * chosen organization's offices, so a rule can't be scoped to another org's
 * office. Omit it to offer every office, which is what products do.
 */
const AudienceSelect = ({ value = EMPTY, onChange, error, offices: officesProp }) => {
  const { data: allOffices = [], isPending: isFetching } = useGetOfficesQuery({
    enabled: !officesProp,
  });
  const offices = officesProp || allOffices;
  const isPending = officesProp ? false : isFetching;

  const mode = value?.mode || "all";
  const selectedIds = value?.offices || [];

  const options = (offices || []).map((o) => ({
    label: o.name,
    value: o._id,
    subLabel: o.address || o.suburb || "",
  }));

  const selectedOptions = options.filter((o) => selectedIds.includes(o.value));

  const handleModeChange = (next) =>
    // Switching back to "all" drops the selection — it's meaningless there,
    // and keeping it would silently reapply on the next toggle.
    onChange(
      next === "all"
        ? { mode: "all", offices: [] }
        : { mode: "specific", offices: selectedIds },
    );

  return (
    <div className="space-y-2">
      <SearchSelect
        options={MODE_OPTIONS}
        value={MODE_OPTIONS.find((o) => o.value === mode) || MODE_OPTIONS[0]}
        onChange={(option) => handleModeChange(option?.value || "all")}
        searchable={false}
      />

      {mode === "specific" && (
        <SearchSelect
          options={options}
          value={selectedOptions}
          onChange={(opts) =>
            onChange({
              mode: "specific",
              // SearchSelect sends null when cleared.
              offices: (opts || []).map((o) => o.value),
            })
          }
          placeholder={isPending ? "Loading..." : "Select offices..."}
          disabled={isPending}
          multiple
        />
      )}

      <p className="text-sm text-darkgray">
        {mode === "all"
          ? "Every office can see and buy this product."
          : "Only agents in the selected offices can see and buy this product."}
      </p>

      {error && <p className="form-error-message">{error}</p>}
    </div>
  );
};

/** Stored `offices` (ids, or populated docs) → form value. */
export const toAudienceValue = (offices) => {
  const ids = (offices || []).map((o) => String(o?._id || o));
  return ids.length ? { mode: "specific", offices: ids } : { ...EMPTY };
};

/** Form value → stored `offices` ([] = all offices). */
export const fromAudienceValue = (audience) =>
  audience?.mode === "specific" ? audience.offices || [] : [];

/** Returns an error message, or "" when the selection is valid. */
export const validateAudience = (audience) =>
  audience?.mode === "specific" && !(audience.offices || []).length
    ? "Select at least one office"
    : "";

/** react-hook-form rule wrapping the same check. */
export const audienceRules = {
  validate: (v) => validateAudience(v) || true,
};

export default AudienceSelect;
